import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SimpleRevealSlider from '../SimpleRevealSlider';

function resolveImageSource(image) {
  if (!image) return '';
  if (typeof image === 'string') return image;
  return image.src || image.path || image.url || image.image_url || '';
}

function resolvePrompt(scan) {
  return (
    scan?.prompt ||
    scan?.metadata?.prompt ||
    scan?.hairstyle_suggestions?.promptUsed ||
    scan?.hairstyle_suggestions?.prompt ||
    ''
  );
}

function shouldHideProvider(provider, hideValues = []) {
  if (!provider) return true;
  const normalized = String(provider).trim().toLowerCase();
  return hideValues.map((value) => String(value).trim().toLowerCase()).includes(normalized);
}

export function AdminAIThumbnailGallery({
  originalImage,
  generatedImages = [],
  onOriginalClick,
  onGeneratedClick,
  generatedLimit = 4,
  showGeneratedEmptyState = false,
}) {
  const limitedImages = generatedImages.slice(0, generatedLimit);

  return (
    <div className="p-4">
      {originalImage ? (
        <div className="mb-4">
          <h4 className="mb-2 flex items-center gap-1 text-xs font-medium text-zinc-700 dark:text-zinc-300">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Original Photo
          </h4>
          <div className="relative group/card cursor-pointer" onClick={onOriginalClick}>
            <img
              src={resolveImageSource(originalImage)}
              alt="Original"
              className="h-40 w-full rounded-xl bg-gray-50 object-contain transition-transform group-hover/card:scale-105 dark:bg-gray-800"
            />
            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/0 transition-all duration-300 group-hover/card:bg-black/10">
              <div className="opacity-0 transition-opacity duration-300 group-hover/card:opacity-100">
                <svg className="h-8 w-8 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {generatedImages.length > 0 ? (
        <div>
          <h4 className="mb-2 flex items-center gap-1 text-xs font-medium text-zinc-700 dark:text-zinc-300">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            AI Generated ({generatedImages.length})
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {limitedImages.map((image, index) => (
              <div
                key={`${resolveImageSource(image) || 'generated'}-${index}`}
                className="relative group/img cursor-pointer"
                onClick={() => onGeneratedClick(image)}
              >
                <img
                  src={resolveImageSource(image)}
                  alt={`Generated ${index + 1}`}
                  className="h-24 w-full rounded-lg bg-gray-50 object-contain transition-transform group-hover/img:scale-105 dark:bg-gray-800"
                />
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 transition-all duration-300 group-hover/img:bg-black/10">
                  <div className="opacity-0 transition-opacity duration-300 group-hover/img:opacity-100">
                    <svg className="h-6 w-6 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
            {generatedImages.length > generatedLimit ? (
              <div className="flex h-24 items-center justify-center rounded-lg border border-white/20 bg-gradient-to-br from-zinc-100 to-zinc-200 dark:border-white/10 dark:from-zinc-700 dark:to-zinc-800">
                <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">+{generatedImages.length - generatedLimit} more</span>
              </div>
            ) : null}
          </div>
        </div>
      ) : showGeneratedEmptyState ? (
        <div>
          <h4 className="mb-2 flex items-center gap-1 text-xs font-medium text-zinc-700 dark:text-zinc-300">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            AI Generated (0)
          </h4>
          <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-white/20 bg-zinc-100 text-xs text-zinc-600 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-400">
            No generated images available
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function AdminAIComparisonModal({
  isOpen,
  onClose,
  activeScan,
  selectedImage,
  clientName,
  showAnalysisInformation = true,
  showProvider = true,
  showStatus = true,
  hideProviderValues = [],
}) {
  return (
    <AnimatePresence>
      {isOpen && selectedImage ? (() => {
        const before = resolveImageSource(activeScan?.originalImage || selectedImage?.path || selectedImage?.src || '');

        let generated = '';
        if (selectedImage?.type === 'generated') {
          generated = resolveImageSource(selectedImage);
        } else if (Array.isArray(activeScan?.generatedImages) && activeScan.generatedImages.length > 0) {
          generated = resolveImageSource(activeScan.generatedImages[0]);
        } else if (Array.isArray(activeScan?.legacyStyledImages) && activeScan.legacyStyledImages.length > 0) {
          generated = resolveImageSource(activeScan.legacyStyledImages[0]);
        } else if (activeScan?.resultUrl) {
          generated = resolveImageSource(activeScan.resultUrl);
        }
        if (!generated) generated = before;

        const prompt = resolvePrompt(activeScan);
        const provider = selectedImage?.provider || activeScan?.provider || activeScan?.hairstyle_suggestions?.provider || '';
        const shouldRenderProvider = showProvider && !shouldHideProvider(provider, hideProviderValues);
        const timestamp =
          selectedImage?.generatedAt ||
          selectedImage?.timestamp ||
          activeScan?.timestamps?.scan_created_at ||
          activeScan?.created_at ||
          null;

        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="relative max-h-[85vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-white/20 bg-white/90 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-zinc-900/90 md:max-w-3xl lg:max-w-4xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-white/20 px-6 py-4 dark:border-white/10">
                <div>
                  <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Before / After</h3>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">{clientName || activeScan?.client?.name || activeScan?.client_name || 'Client'}</p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-white/20 bg-white/50 p-2 text-zinc-600 transition-all hover:bg-white/70 dark:border-white/10 dark:bg-zinc-800/50 dark:text-zinc-300 dark:hover:bg-zinc-800/70"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-5">
                <SimpleRevealSlider
                  beforeSrc={before}
                  afterSrc={generated}
                  altBefore="Original"
                  altAfter="Generated"
                  className="mx-auto aspect-[3/4] max-w-[520px]"
                  imageFit="contain"
                />

                {prompt ? (
                  <div className="mt-6 rounded-xl border border-white/20 bg-white/30 p-4 backdrop-blur-sm dark:border-white/10 dark:bg-zinc-800/30">
                    <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">AI Prompt Used</h4>
                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">{prompt}</p>
                  </div>
                ) : null}

                {showAnalysisInformation ? (
                  <div className="mt-6 rounded-xl border border-white/20 bg-white/30 p-4 backdrop-blur-sm dark:border-white/10 dark:bg-zinc-800/30">
                    <h4 className="mb-3 text-lg font-semibold text-zinc-900 dark:text-zinc-100">Analysis Information</h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {(selectedImage?.faceShape || activeScan?.faceShape || activeScan?.face_shape || activeScan?.hairstyle_suggestions?.faceShape) ? (
                        <div>
                          <span className="font-medium text-zinc-600 dark:text-zinc-400">Face Shape:</span>
                          <span className="ml-2 text-zinc-900 dark:text-zinc-100">
                            {selectedImage?.faceShape || activeScan?.faceShape || activeScan?.face_shape || activeScan?.hairstyle_suggestions?.faceShape}
                          </span>
                        </div>
                      ) : null}
                      {(selectedImage?.haircut || activeScan?.haircut) ? (
                        <div>
                          <span className="font-medium text-zinc-600 dark:text-zinc-400">Haircut Style:</span>
                          <span className="ml-2 text-zinc-900 dark:text-zinc-100">{selectedImage?.haircut || activeScan?.haircut}</span>
                        </div>
                      ) : null}
                      {(selectedImage?.hair_color || activeScan?.hair_color) ? (
                        <div>
                          <span className="font-medium text-zinc-600 dark:text-zinc-400">Hair Color:</span>
                          <span className="ml-2 text-zinc-900 dark:text-zinc-100">{selectedImage?.hair_color || activeScan?.hair_color}</span>
                        </div>
                      ) : null}
                      {activeScan?.credits_used ? (
                        <div>
                          <span className="font-medium text-zinc-600 dark:text-zinc-400">Credits Used:</span>
                          <span className="ml-2 text-zinc-900 dark:text-zinc-100">{activeScan.credits_used}</span>
                        </div>
                      ) : null}
                      {shouldRenderProvider ? (
                        <div>
                          <span className="font-medium text-zinc-600 dark:text-zinc-400">Provider:</span>
                          <span className="ml-2 text-zinc-900 dark:text-zinc-100">{provider}</span>
                        </div>
                      ) : null}
                      {showStatus && (activeScan?.generation_status || activeScan?.status) ? (
                        <div>
                          <span className="font-medium text-zinc-600 dark:text-zinc-400">Status:</span>
                          <span className="ml-2 text-zinc-900 dark:text-zinc-100">{activeScan?.generation_status || activeScan?.status}</span>
                        </div>
                      ) : null}
                      {timestamp ? (
                        <div>
                          <span className="font-medium text-zinc-600 dark:text-zinc-400">Generated:</span>
                          <span className="ml-2 text-zinc-900 dark:text-zinc-100">{new Date(timestamp).toLocaleString()}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        );
      })() : null}
    </AnimatePresence>
  );
}
